import { useCallback, useEffect, useState } from 'react';

import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import type { UploadedFile } from 'js/src/components/sketch/FileUpload';
import { isUploadedMedia } from 'js/src/components/sketch/FileUpload';
import { ErrorReporter } from 'js/src/libs/error-reporter';

const CHAT_HISTORY_KEY = 'chat-last-input';

export interface ChatHistoryEntry {
    text: string;
    files: UploadedFile[];
}

/**
 * Load last chat message from localStorage
 */
function loadLastEntry(): ChatHistoryEntry | null {
    try {
        const stored = localStorage.getItem(CHAT_HISTORY_KEY);
        if (stored === null) {
            return null;
        }
        try {
            const parsed = JSON.parse(stored) as unknown;
            if (parsed && typeof parsed === 'object' && 'text' in parsed) {
                const entry = parsed as ChatHistoryEntry;
                return { text: entry.text ?? '', files: entry.files ?? [] };
            }
        } catch {
            // Fall through: legacy entries were stored as a bare text string
        }
        return { text: stored, files: [] };
    } catch {
        return null;
    }
}

/**
 * Save last chat message to localStorage
 */
function saveLastEntry(entry: ChatHistoryEntry): void {
    try {
        localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(entry));
    } catch {
        // Silently fail if localStorage is not available
    }
}

/**
 * Re-register recalled media with the MediaRegistry by UUID. The persisted
 * `mediaRegistryId` is a session-local handle into an in-memory singleton that
 * is empty on a fresh page load, so it must be refreshed from the UUID before
 * the chat can reference the media. Documents and audio don't rely on the
 * registry, so they pass through unchanged.
 */
async function rehydrateRecalledFiles(files: UploadedFile[]): Promise<UploadedFile[]> {
    const mediaRegistry = MediaRegistry.getInstance();
    const rehydrated: UploadedFile[] = [];

    for (const file of files) {
        if (!isUploadedMedia(file) || !file.uuid) {
            rehydrated.push(file);
            continue;
        }
        try {
            const mediaRegistryId = await mediaRegistry.addItemByUuid(file.uuid);
            if (mediaRegistryId) {
                rehydrated.push({ ...file, mediaRegistryId });
            }
            // If addItemByUuid returns null the asset is gone — drop the file.
        } catch (error) {
            // Partial success is better than dropping the whole recall.
            if (error instanceof Error) {
                ErrorReporter.capture(error);
            }
        }
    }

    return rehydrated;
}

interface UseChatHistoryOptions {
    onHistoryChange: (entry: ChatHistoryEntry) => void;
}

interface UseChatHistoryReturn {
    recallLast: () => void;
    saveToHistory: (message: string, files?: UploadedFile[]) => void;
}

/**
 * Hook for managing chat history - stores only the last message
 */
export function useChatHistory({ onHistoryChange }: UseChatHistoryOptions): UseChatHistoryReturn {
    const [lastEntry, setLastEntry] = useState<ChatHistoryEntry | null>(null);

    // Load last message on mount
    useEffect(() => {
        setLastEntry(loadLastEntry());
    }, []);

    const recallLast = useCallback(() => {
        if (!lastEntry) {
            return;
        }
        if (lastEntry.files.length === 0) {
            onHistoryChange(lastEntry);
            return;
        }
        void rehydrateRecalledFiles(lastEntry.files).then((files) => {
            onHistoryChange({ text: lastEntry.text, files });
        });
    }, [lastEntry, onHistoryChange]);

    const saveToHistory = useCallback((message: string, files: UploadedFile[] = []) => {
        if (!message.trim() && files.length === 0) {
            return;
        }
        const entry: ChatHistoryEntry = { text: message, files };
        saveLastEntry(entry);
        setLastEntry(entry);
    }, []);

    return {
        recallLast,
        saveToHistory,
    };
}
